import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useSnapshot } from 'valtio';
import { toast } from 'react-toastify';
import { authStore } from '../../../store/authStore';
import { ADMIN_DEFAULT_LANDING } from '../../../constants/adminNav';
import { getCurrentRuntimeTarget } from '../../../config/runtime-target';
import {
  APP_FEATURE_HISTORY,
  APP_FEATURE_MATRIX,
  SET_BRAND_APP_FEATURE,
} from '../../services/appFeaturesService/queries';
import SwitchComponent from '../../components/switch';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '../../components/ui';
import {
  ENVIRONMENT_STAGING_NOTICE,
  STALE_MESSAGE,
  appFeatureRows,
  cellChangedText,
  cellKey,
  cellOf,
  defaultEnvironmentLabel,
  describeChange,
  environmentCardEditable,
  environmentConfirmText,
  environmentRows,
  actorName,
  errorMessage,
  formatWhen,
  isStaleError,
  neverChangedText,
  serverNotice,
  stagingWebHostOf,
  toggleInput,
  type AppFeatureChange,
  type AppFeatureMatrix,
} from './appFeatures';

const HISTORY_PAGE = 20;

const FeatureName = styled.div`
  ${tw`font-medium`}
`;

const FeatureHint = styled.div`
  ${tw`text-xs mt-1`}
  max-width: 320px;
  color: var(--color-text-secondary);
`;

const BrandHead = styled.div`
  ${tw`normal-case tracking-normal`}
`;

const CellInner = styled.div`
  ${tw`flex items-center gap-2`}
`;

const Segmented = styled.div`
  ${tw`inline-flex rounded-md overflow-hidden`}
  border: 1px solid var(--color-border);
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  ${tw`px-3 py-1 text-xs font-medium`}
  background: ${({ $active }) => ($active ? 'var(--color-black)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--color-on-primary, #fff)' : 'inherit')};
  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const HistoryList = styled.ul`
  ${tw`px-5`}
  list-style: none;
`;

const HistoryRow = styled.li`
  ${tw`py-3 text-sm`}
  & + & {
    border-top: 1px solid var(--color-border);
  }
`;

const HistoryMeta = styled.div`
  ${tw`text-xs mt-1`}
  color: var(--color-text-secondary);
`;

/**
 * Spec 021, platform owner only: which mobile app features each brand's app
 * shows. Spec 027 adds the environment card (default environment and testing
 * banner), editable on production only. Every feature is off until turned on here. A click saves one cell
 * with the value the page showed, so a change made by someone else in the
 * meantime is reported instead of overwritten.
 */
export default function AdminAppFeaturesPage() {
  const { user } = useSnapshot(authStore);
  const isPlatformOwner = Boolean((user as any)?.isPlatformOwner);

  const matrixQuery = useQuery(APP_FEATURE_MATRIX, {
    fetchPolicy: 'network-only',
    skip: !isPlatformOwner,
  });
  const historyQuery = useQuery(APP_FEATURE_HISTORY, {
    variables: { limit: HISTORY_PAGE },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !isPlatformOwner,
  });
  const [setFeature] = useMutation(SET_BRAND_APP_FEATURE);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [historyEnd, setHistoryEnd] = useState(false);

  if (!isPlatformOwner) {
    return <Navigate to={ADMIN_DEFAULT_LANDING} replace />;
  }

  const matrix: AppFeatureMatrix | null = matrixQuery.data?.appFeatureMatrix ?? null;
  const history: AppFeatureChange[] = historyQuery.data?.appFeatureHistory ?? [];

  const target = getCurrentRuntimeTarget();
  const environmentEditable = environmentCardEditable(target);
  const featureRows = matrix ? appFeatureRows(matrix) : [];
  const envRows = matrix ? environmentRows(matrix) : [];

  const toggle = async (brandSite: string, feature: string) => {
    if (!matrix) return;
    const current = cellOf(matrix, brandSite, feature);
    const brandDisplay =
      matrix.brands.find(row => row.brandSite === brandSite)?.displayName || brandSite;
    const confirmText = environmentConfirmText(feature, !current.enabled, brandDisplay, {
      brandSite,
      stagingWebHost: stagingWebHostOf(brandSite),
    });
    if (confirmText && !window.confirm(confirmText)) return;
    const key = cellKey(brandSite, feature);
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await setFeature({ variables: { input: toggleInput(cellOf(matrix, brandSite, feature)) } });
      await Promise.all([matrixQuery.refetch(), historyQuery.refetch()]);
      setHistoryEnd(false);
    } catch (error) {
      if (isStaleError(error)) {
        toast.warning(STALE_MESSAGE);
        await Promise.all([matrixQuery.refetch(), historyQuery.refetch()]);
      } else {
        toast.error(errorMessage(error));
      }
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const loadMoreHistory = async () => {
    const last = history[history.length - 1];
    if (!last) return;
    const result = await historyQuery.fetchMore({
      variables: { limit: HISTORY_PAGE, before: last.id },
      updateQuery: (prev, { fetchMoreResult }) => ({
        appFeatureHistory: [
          ...(prev?.appFeatureHistory ?? []),
          ...(fetchMoreResult?.appFeatureHistory ?? []),
        ],
      }),
    });
    if ((result.data?.appFeatureHistory ?? []).length < HISTORY_PAGE) {
      setHistoryEnd(true);
    }
  };

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title="App features"
        description="Choose which mobile app features each brand’s app shows. Every feature is off until you turn it on. Brand admins cannot see or change this page."
      />
      <Stack>
        <Alert tone="neutral" data-testid="app-features-server">
          {serverNotice(target)} Apps pick up a change the next time they open Profile or restart.
        </Alert>

        <Card>
          <CardHeader title="Features by brand" />
          {matrixQuery.loading && !matrix ? (
            <LoadingBlock label="Loading app features" />
          ) : matrixQuery.error && !matrix ? (
            <CardBody>
              <Alert tone="error">{errorMessage(matrixQuery.error)}</Alert>
            </CardBody>
          ) : matrix && matrix.brands.length > 0 ? (
            <CardBody $padding="none">
              <TableWrap>
                <Table data-testid="app-features-matrix">
                  <THead>
                    <TR>
                      <TH>Feature</TH>
                      {matrix.brands.map(brand => (
                        <TH key={brand.brandSite}>
                          <BrandHead>
                            <div>{brand.displayName}</div>
                            <Muted $size="xs">{brand.brandSite}</Muted>
                          </BrandHead>
                        </TH>
                      ))}
                    </TR>
                  </THead>
                  <TBody>
                    {featureRows.map(feature => (
                      <TR key={feature.key} data-testid={`app-feature-row-${feature.key}`}>
                        <TD>
                          <FeatureName>{feature.label}</FeatureName>
                          <FeatureHint>{feature.description}</FeatureHint>
                        </TD>
                        {matrix.brands.map(brand => {
                          const cell = cellOf(matrix, brand.brandSite, feature.key);
                          const key = cellKey(brand.brandSite, feature.key);
                          return (
                            <TD
                              key={key}
                              title={cellChangedText(cell) ?? neverChangedText(cell.enabled)}
                            >
                              <CellInner>
                                <SwitchComponent
                                  isActive={cell.enabled}
                                  disabled={Boolean(saving[key])}
                                  ariaLabel={`${feature.label} on ${brand.displayName}`}
                                  inputTestId={`app-feature-${brand.brandSite}-${feature.key}`}
                                  onChange={() => toggle(brand.brandSite, feature.key)}
                                />
                                <Muted $size="xs">{cell.enabled ? 'Shown' : 'Hidden'}</Muted>
                              </CellInner>
                            </TD>
                          );
                        })}
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            </CardBody>
          ) : (
            <EmptyState compact title="No brands yet" />
          )}
        </Card>

        {envRows.length > 0 && (
          <Card data-testid="app-environment-card">
            <CardHeader
              title="Environment (apps)"
              description="Which environment each brand's app opens on, and whether testing shows the testing mode banner. Apps and staging sites read these from production. A user's own choice in Profile → Environment wins. A brand with its own testing site (Lenvoy) moves its website too: visitors go to the testing site, except on admin pages and for people who opened the site with ?env=production."
            />
            {!environmentEditable ? (
              <CardBody>
                <Alert tone="neutral" data-testid="app-environment-staging-notice">
                  {ENVIRONMENT_STAGING_NOTICE}
                </Alert>
              </CardBody>
            ) : matrix ? (
              <CardBody $padding="none">
                <TableWrap>
                  <Table data-testid="app-environment-matrix">
                    <THead>
                      <TR>
                        <TH>Setting</TH>
                        {matrix.brands.map(brand => (
                          <TH key={brand.brandSite}>
                            <BrandHead>
                              <div>{brand.displayName}</div>
                              <Muted $size="xs">{brand.brandSite}</Muted>
                            </BrandHead>
                          </TH>
                        ))}
                      </TR>
                    </THead>
                    <TBody>
                      {envRows.map(feature => (
                        <TR key={feature.key} data-testid={`app-environment-row-${feature.key}`}>
                          <TD>
                            <FeatureName>
                              {feature.key === 'stagingByDefault'
                                ? 'Default environment'
                                : feature.label}
                            </FeatureName>
                            <FeatureHint>{feature.description}</FeatureHint>
                          </TD>
                          {matrix.brands.map(brand => {
                            const cell = cellOf(matrix, brand.brandSite, feature.key);
                            const key = cellKey(brand.brandSite, feature.key);
                            const busy = Boolean(saving[key]);
                            const title = cellChangedText(cell) ?? neverChangedText(cell.enabled);
                            if (feature.key === 'stagingByDefault') {
                              return (
                                <TD key={key} title={title}>
                                  <Segmented
                                    role="group"
                                    aria-label={`Default environment on ${brand.displayName}`}
                                  >
                                    {[false, true].map(staging => (
                                      <SegmentButton
                                        key={String(staging)}
                                        type="button"
                                        $active={cell.enabled === staging}
                                        aria-pressed={cell.enabled === staging}
                                        disabled={busy || cell.enabled === staging}
                                        data-testid={`app-environment-${brand.brandSite}-${
                                          staging ? 'staging' : 'production'
                                        }`}
                                        onClick={() => toggle(brand.brandSite, feature.key)}
                                      >
                                        {defaultEnvironmentLabel(staging)}
                                      </SegmentButton>
                                    ))}
                                  </Segmented>
                                </TD>
                              );
                            }
                            return (
                              <TD key={key} title={title}>
                                <CellInner>
                                  <SwitchComponent
                                    isActive={cell.enabled}
                                    disabled={busy}
                                    ariaLabel={`${feature.label} on ${brand.displayName}`}
                                    inputTestId={`app-environment-${brand.brandSite}-${feature.key}`}
                                    onChange={() => toggle(brand.brandSite, feature.key)}
                                  />
                                  <Muted $size="xs">{cell.enabled ? 'Shown' : 'Hidden'}</Muted>
                                </CellInner>
                              </TD>
                            );
                          })}
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </TableWrap>
              </CardBody>
            ) : null}
          </Card>
        )}

        <Card>
          <CardHeader title="Change history" description="Every change, newest first." />
          {historyQuery.loading && history.length === 0 ? (
            <LoadingBlock label="Loading history" minHeight="80px" />
          ) : history.length === 0 ? (
            <EmptyState
              compact
              title="No changes yet"
              description="Every setting still has its default."
            />
          ) : (
            <>
              <HistoryList data-testid="app-features-history">
                {history.map(change => (
                  <HistoryRow key={change.id}>
                    <div>{describeChange(change, matrix)}</div>
                    <HistoryMeta>
                      {actorName(change.changedBy)} · {formatWhen(change.changedAt)}
                    </HistoryMeta>
                  </HistoryRow>
                ))}
              </HistoryList>
              {!historyEnd && history.length >= HISTORY_PAGE && (
                <CardFooter>
                  <Button
                    theme="secondary"
                    size="sm"
                    type="button"
                    text="Show older changes"
                    onClick={loadMoreHistory}
                    isLoading={historyQuery.loading}
                  />
                </CardFooter>
              )}
            </>
          )}
        </Card>
      </Stack>
    </PageContainer>
  );
}
